<template>
  <div
    class="flex w-full items-center"
    :class="horizontal ? 'flex-row gap-x-2' : 'flex-col gap-y-2'"
  >
    <div class="flex w-full flex-col gap-y-2">
      <component
        :is="horizontal ? Input : Textarea"
        ref="inputRef"
        v-model="comment"
        :placeholder="placeholder || $t('common.addAComment')"
        class="w-full"
        @keydown.enter.prevent="emit('submit')"
      />
      <InfoBar v-if="error" variant="destructive" :title="error" :message="''" />
    </div>
    <Button
      :disabled="isSending"
      type="submit"
      @click="emit('submit')"
      :class="horizontal ? 'w-max' : 'w-full'"
    >
      <Loader2 v-if="isSending" class="mr-2 h-4 w-4 animate-spin stroke-white" />
      {{ buttonLabel || t('common.send') }}
    </Button>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ComponentPublicInstance } from 'vue';

// Types
type InputRefType = ComponentPublicInstance & {
  inputRef?: {
    value: HTMLInputElement | HTMLTextAreaElement | null;
  };
};

// Components
import Button from '@ui/button/Button.vue';
import Textarea from '@ui/textarea/Textarea.vue';
import Input from '@ui/input/Input.vue';
import InfoBar from '@/components/generic/InfoBar.vue';

// Icons
import { Loader2 } from 'lucide-vue-next';

// Refs
const inputRef = ref<InputRefType | null>(null);

// Props
defineProps<{
  isSending?: boolean;
  horizontal?: boolean;
  placeholder?: string;
  buttonLabel?: string;
  error?: string;
}>();

// Composables
const { t } = useI18n();

// Models
const comment = defineModel<string>('comment', {
  required: true,
});

// Methods
const focus = () => {
  inputRef.value?.$el.focus();
};

defineExpose({
  focus,
});

const emit = defineEmits(['submit']);
</script>
